<script setup>
// Campos do bloco selecionado: variante, textos, itens, imagem e estilo.
// Os campos "ricos" aceitam marcação leve (<b>, <a>, listas) que o servidor
// sanitiza; o resto é texto puro.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MediaLibraryPanel from 'dashboard/routes/dashboard/marketing/pages/MediaLibraryPanel.vue';

const props = defineProps({
  block: { type: Object, required: true },
  catalog: { type: Object, default: null },
  theme: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update', 'remove', 'duplicate']);
const { t } = useI18n();

// Campos por tipo: [nome, tipo] — text | rich | url | bool.
const FIELDS = {
  header: [['show_browser_link', 'bool']],
  hero: [
    ['headline', 'text'],
    ['subheadline', 'rich'],
    ['cta_label', 'text'],
    ['cta_url', 'url'],
  ],
  text: [['html', 'rich']],
  button: [
    ['label', 'text'],
    ['url', 'url'],
  ],
  columns: [
    ['headline', 'text'],
    ['text', 'rich'],
  ],
  offer: [
    ['headline', 'text'],
    ['price', 'text'],
    ['description', 'rich'],
    ['cta_label', 'text'],
    ['cta_url', 'url'],
  ],
  testimonial: [
    ['quote', 'rich'],
    ['author', 'text'],
    ['role', 'text'],
  ],
  benefits: [['headline', 'text']],
  event: [
    ['headline', 'text'],
    ['date_text', 'text'],
    ['time_text', 'text'],
    ['location', 'text'],
    ['cta_label', 'text'],
    ['cta_url', 'url'],
  ],
  signature: [
    ['name', 'text'],
    ['role', 'text'],
    ['phone', 'text'],
    ['email', 'text'],
  ],
  divider: [],
  footer: [
    ['company', 'text'],
    ['address', 'text'],
    ['note', 'rich'],
    ['unsubscribe_label', 'text'],
  ],
};
const ITEM_KINDS = ['columns', 'benefits'];
// Variáveis mostradas na dica (fora do i18n: vue-i18n não aceita {{ }} na string).
const VARS_HINT = '{{primeiro_nome}}, {{empresa}}, {{vendedor}}';

const fields = computed(() => FIELDS[props.block.kind] || []);
const variants = computed(
  () => props.catalog?.variants?.[props.block.kind] || []
);
const mediaKinds = computed(() => props.catalog?.media_kinds || []);
const hasMedia = computed(() => mediaKinds.value.includes(props.block.kind));
const hasItems = computed(() => ITEM_KINDS.includes(props.block.kind));
const showItems = computed(
  () =>
    hasItems.value &&
    !(props.block.kind === 'columns' && props.block.variant !== 'two_cards')
);
const style = computed(() => props.block.style || {});
const items = computed(() => props.block.items || []);
const HEX = /^#[0-9a-f]{6}$/i;
// Fundo em hex aparece como "Personalizada" no select, com o seletor de cor.
const customBg = computed(() => HEX.test(style.value.background || ''));
const hasButton = computed(() =>
  (props.catalog?.button_kinds || []).includes(props.block.kind)
);

const up = patch => emit('update', patch);
const setStyle = patch => up({ style: { ...style.value, ...patch } });
const onBackground = value =>
  setStyle({
    background: value === 'custom' ? props.theme.surface || '#f5f7fa' : value,
  });
// Voltar à cor do tema = tirar as chaves (o servidor só aceita hex).
const resetButtonColors = () => {
  const { button_color: _bc, button_text: _bt, ...rest } = style.value;
  up({ style: rest });
};
// Amostra do texto do botão = a cor que o e-mail usa. Sem escolha, o
// servidor (RenderContext#button_fg) decide pelo contraste da cor do botão.
const luminance = hex => {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const buttonTextSwatch = computed(() => {
  if (style.value.button_text) return style.value.button_text;
  const bg = style.value.button_color;
  return bg && HEX.test(bg) && luminance(bg) >= 0.6 ? '#111827' : '#ffffff';
});
const setItem = (i, patch) => {
  const next = items.value.map((it, idx) =>
    idx === i ? { ...it, ...patch } : it
  );
  up({ items: next });
};
const addItem = () => up({ items: [...items.value, { title: '', text: '' }] });
const removeItem = i =>
  up({ items: items.value.filter((_, idx) => idx !== i) });

const mediaDialog = ref(null);
const pickMedia = asset => {
  up({
    media: { ...(props.block.media || {}), asset_id: asset.id, url: asset.url },
  });
  mediaDialog.value?.close();
};
const clearMedia = () => up({ media: { asset_id: null, url: null } });

const label = key => t(`EMAIL_TEMPLATES.BUILDER.FIELDS.${key}`);
const variantLabel = v => t(`EMAIL_TEMPLATES.BUILDER.VARIANTS.${v}`);
const inputClass =
  'w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand';
</script>

<template>
  <div class="flex flex-col gap-4 p-3">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t(`EMAIL_TEMPLATES.BUILDER.BLOCKS.${block.kind}`) }}
      </h3>
      <div class="flex items-center gap-1">
        <button
          v-if="!['header', 'signature', 'footer'].includes(block.kind)"
          type="button"
          class="i-lucide-copy text-n-slate-10 hover:text-n-slate-12"
          :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.DUPLICATE_BLOCK')"
          @click="emit('duplicate')"
        />
        <button
          v-if="block.kind !== 'footer'"
          type="button"
          class="i-lucide-trash-2 text-n-slate-10 hover:text-n-ruby-9"
          :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.REMOVE_BLOCK')"
          @click="emit('remove')"
        />
      </div>
    </div>

    <!-- Variante -->
    <div v-if="variants.length > 1" class="flex flex-wrap gap-1.5">
      <button
        v-for="v in variants"
        :key="v"
        type="button"
        class="rounded-lg border px-2.5 py-1 text-xs"
        :class="
          block.variant === v
            ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
            : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'
        "
        @click="up({ variant: v })"
      >
        {{ variantLabel(v) }}
      </button>
    </div>

    <!-- Campos -->
    <label v-for="[key, type] in fields" :key="key" class="flex flex-col gap-1">
      <span class="text-xs text-n-slate-10">{{ label(key) }}</span>
      <template v-if="type === 'bool'">
        <input
          type="checkbox"
          class="size-4 accent-[#1f93ff]"
          :checked="block[key] !== false"
          @change="up({ [key]: $event.target.checked })"
        />
      </template>
      <textarea
        v-else-if="type === 'rich'"
        :value="block[key]"
        rows="4"
        :class="inputClass"
        @input="up({ [key]: $event.target.value })"
      />
      <input
        v-else
        :type="type === 'url' ? 'url' : 'text'"
        :value="block[key]"
        :class="inputClass"
        @input="up({ [key]: $event.target.value })"
      />
    </label>
    <p
      v-if="fields.some(([, type]) => type === 'rich')"
      class="text-[11px] text-n-slate-10"
    >
      {{ t('EMAIL_TEMPLATES.BUILDER.RICH_HINT', { vars: VARS_HINT }) }}
    </p>

    <!-- Itens (colunas em cards, benefícios) -->
    <div v-if="showItems" class="flex flex-col gap-2">
      <span class="text-xs text-n-slate-10">{{
        t('EMAIL_TEMPLATES.BUILDER.FIELDS.items')
      }}</span>
      <div
        v-for="(it, i) in items"
        :key="i"
        class="flex flex-col gap-1.5 rounded-lg border border-n-weak p-2"
      >
        <input
          :value="it.title"
          :placeholder="label('title')"
          :class="inputClass"
          @input="setItem(i, { title: $event.target.value })"
        />
        <input
          :value="it.text"
          :placeholder="label('text')"
          :class="inputClass"
          @input="setItem(i, { text: $event.target.value })"
        />
        <button
          type="button"
          class="self-end text-[11px] text-n-ruby-9"
          @click="removeItem(i)"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.ACTIONS.REMOVE_ITEM') }}
        </button>
      </div>
      <button
        v-if="items.length < 8"
        type="button"
        class="rounded-lg border border-dashed border-n-weak py-1.5 text-xs text-n-slate-11 hover:border-n-brand hover:text-n-brand"
        @click="addItem"
      >
        + {{ t('EMAIL_TEMPLATES.BUILDER.ACTIONS.ADD_ITEM') }}
      </button>
    </div>

    <!-- Imagem -->
    <div v-if="hasMedia" class="flex flex-col gap-2">
      <span class="text-xs text-n-slate-10">{{
        t('EMAIL_TEMPLATES.BUILDER.FIELDS.media')
      }}</span>
      <img
        v-if="block.media?.url"
        :src="block.media.url"
        alt=""
        class="max-h-32 w-full rounded-lg border border-n-weak object-cover"
      />
      <div class="flex gap-2">
        <button
          type="button"
          class="rounded-lg border border-n-weak px-2.5 py-1 text-xs text-n-slate-11 hover:bg-n-solid-2"
          @click="mediaDialog?.open()"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.ACTIONS.PICK_IMAGE') }}
        </button>
        <button
          v-if="block.media?.url"
          type="button"
          class="text-xs text-n-ruby-9"
          @click="clearMedia"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.ACTIONS.REMOVE_IMAGE') }}
        </button>
      </div>
    </div>

    <!-- Estilo -->
    <div class="flex flex-col gap-2 border-t border-n-weak pt-3">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.BUILDER.STYLE.TITLE')
      }}</span>
      <label
        class="flex items-center justify-between gap-2 text-xs text-n-slate-10"
      >
        {{ t('EMAIL_TEMPLATES.BUILDER.STYLE.BACKGROUND') }}
        <span class="flex items-center gap-1.5">
          <input
            v-if="customBg"
            type="color"
            :value="style.background"
            :title="t('EMAIL_TEMPLATES.BUILDER.STYLE.BG.custom')"
            class="size-6 cursor-pointer rounded border-0 bg-transparent p-0"
            @input="setStyle({ background: $event.target.value })"
          />
          <select
            :value="customBg ? 'custom' : style.background"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-xs text-n-slate-12"
            @change="onBackground($event.target.value)"
          >
            <option
              v-for="bg in [...(catalog?.backgrounds || []), 'custom']"
              :key="bg"
              :value="bg"
            >
              {{ t(`EMAIL_TEMPLATES.BUILDER.STYLE.BG.${bg}`) }}
            </option>
          </select>
        </span>
      </label>
      <template v-if="hasButton">
        <label
          class="flex items-center justify-between gap-2 text-xs text-n-slate-10"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.STYLE.BUTTON_COLOR') }}
          <input
            type="color"
            :value="style.button_color || theme.primary || '#1f93ff'"
            class="size-6 cursor-pointer rounded border-0 bg-transparent p-0"
            @input="setStyle({ button_color: $event.target.value })"
          />
        </label>
        <label
          class="flex items-center justify-between gap-2 text-xs text-n-slate-10"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.STYLE.BUTTON_TEXT') }}
          <input
            type="color"
            :value="buttonTextSwatch"
            class="size-6 cursor-pointer rounded border-0 bg-transparent p-0"
            @input="setStyle({ button_text: $event.target.value })"
          />
        </label>
        <button
          v-if="style.button_color || style.button_text"
          type="button"
          class="self-end text-[11px] text-n-slate-11 hover:text-n-brand"
          @click="resetButtonColors"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.STYLE.BUTTON_RESET') }}
        </button>
      </template>
      <label
        class="flex items-center justify-between gap-2 text-xs text-n-slate-10"
      >
        {{ t('EMAIL_TEMPLATES.BUILDER.STYLE.ALIGN') }}
        <select
          :value="style.align"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-xs text-n-slate-12"
          @change="setStyle({ align: $event.target.value })"
        >
          <option v-for="a in catalog?.aligns || []" :key="a" :value="a">
            {{ t(`EMAIL_TEMPLATES.BUILDER.STYLE.ALIGNS.${a}`) }}
          </option>
        </select>
      </label>
      <label
        class="flex items-center justify-between gap-2 text-xs text-n-slate-10"
      >
        {{ t('EMAIL_TEMPLATES.BUILDER.STYLE.SPACING') }}
        <select
          :value="style.spacing"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-xs text-n-slate-12"
          @change="setStyle({ spacing: $event.target.value })"
        >
          <option v-for="sp in catalog?.spacings || []" :key="sp" :value="sp">
            {{ t(`EMAIL_TEMPLATES.BUILDER.STYLE.SPACINGS.${sp}`) }}
          </option>
        </select>
      </label>
    </div>

    <Dialog
      ref="mediaDialog"
      :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.PICK_IMAGE')"
      :show-confirm-button="false"
      width="2xl"
      overflow-y-auto
    >
      <MediaLibraryPanel pickable @pick="pickMedia" />
    </Dialog>
  </div>
</template>
